// Copyright 1999-2025. WebPros International GmbH. All rights reserved.

import React, { useContext, useEffect, useRef, useState } from 'react';
import {
    Button,
    ContentLoader,
    Dropdown,
    Heading,
    Icon, ListAction,
    Menu,
    TextArea, Toolbar, ToolbarExpander,
    useTranslate,
} from '@plesk/ui-library';
import { Locale } from 'jsw';

import { useSearchParams } from 'react-router-dom';
import { Answer, CopilotContext } from '../../CopilotContext';
import PopoverConfirm from './PopoverConfirm';
import MessageItem from 'components/layouts/PageLayout/PageHeader/Copilot/MessageItem';
import parseJsonChunks from './parseJsonChunks';

enum Role {
    User = 'user',
    Assistant = 'assistant',
}

const STREAM_OBJECT_DELAY_MS = 10;
const Translate = Locale.getTranslate('components.copilot');

const Chat = () => {
    const translate = useTranslate('components.copilot');
    const { setIsRightSidebarOpen, isRightSidebarOpen, answers, setAnswers } = useContext(CopilotContext);


    const [searchParams, setSearchParams] = useSearchParams();
    const copilotParam = searchParams.get('copilot');

    const [question, setQuestion] = useState('');
    const listRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLFormElement>(null);

    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (listRef.current) {
            listRef.current.scrollTop = listRef.current.scrollHeight;
        }
        if (!loading) {
            inputRef.current?.getElementsByTagName('textarea')[0]?.focus();
        }
    }, [answers, loading]);

    useEffect(() => {
        if (isRightSidebarOpen) {
            inputRef.current?.getElementsByTagName('textarea')[0]?.focus();
        }
    }, [isRightSidebarOpen]);

    const getErrorResponse = (status: number): Response => new Response(
        JSON.stringify({ error: translate('service-unavailable') }), {
            status,
            headers: { 'Content-Type': 'application/json' },
        });

    const getAnswerResponse = async (query: string): Promise<Response> => {
        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
        };
        const tokenEl = document.getElementById('forgery_protection_token') as HTMLMetaElement | null;
        if (tokenEl) {
            headers['X-Forgery-Protection-Token'] = tokenEl.content;
        }
        setLoading(true);
        try {
            const resp = await fetch('/admin/copilot/gen-answer', {
                method: 'POST',
                body: JSON.stringify({ query }),
                headers,
                credentials: 'same-origin',
            });
            if (!resp.ok) {
                return getErrorResponse(resp.status);
            }
            return resp;
        } catch {
            return getErrorResponse(503);
        }
    };

    const processNonStreamResponse = async (
        resp: Response,
        newAnswers: Answer[],
    ) => {
        const respData = await resp.json();
        const answersWithAnswer: Answer[] = [
            ...newAnswers,
            {
                role: Role.Assistant,
                content: respData?.answer ?? undefined,
                error: respData?.error ?? undefined,
                id: newAnswers.length,
            },
        ];
        setAnswers(answersWithAnswer);
        setLoading(false);
        inputRef.current?.focus();
    };

    const processStreamResponse = async (
        resp: Response,
        newAnswers: Answer[],
    ) => {
        const reader = resp.body?.getReader();
        if (!reader) {
            setLoading(false);
            return;
        }
        const decoder = new TextDecoder('utf-8');
        let lastAnswer = newAnswers.length > 0 ? newAnswers[newAnswers.length - 1] : undefined;
        let noResponse = true;
        let buffer = '';
        let loadingCleared = false;

        while (true) {
            const { value, done } = await reader.read();
            if (done) {
                break;
            }
            const chunk = decoder.decode(value, { stream: true });

            const { objects: chunkObjects, buffer: newBuffer } = parseJsonChunks(chunk, buffer);
            buffer = newBuffer;
            if (chunkObjects.length > 0) {
                noResponse = false;
            }

            for (const chunkObject of chunkObjects) {
                // A short delay for smoother text streaming in the UI
                await new Promise(resolve => setTimeout(resolve, STREAM_OBJECT_DELAY_MS));
                const messageContent: string = chunkObject.message.content;
                if (messageContent === '') {
                    continue;
                }

                if (lastAnswer && lastAnswer.role === Role.Assistant) {
                    lastAnswer = {
                        ...lastAnswer,
                        content: (lastAnswer.content ?? '') + messageContent,
                    };
                    newAnswers[newAnswers.length - 1] = lastAnswer;
                } else {
                    lastAnswer = {
                        role: Role.Assistant,
                        content: messageContent,
                        id: newAnswers.length,
                    };
                    newAnswers.push(lastAnswer);
                }
                setAnswers([...newAnswers]);

                if (!loadingCleared) {
                    setLoading(false);
                    loadingCleared = true;
                }
                inputRef.current?.focus();
            }
        }
        if (noResponse) {
            newAnswers.push({
                role: Role.Assistant,
                error: String(translate('service-unavailable')),
                id: newAnswers.length,
            });
            setAnswers([...newAnswers]);
        }
        setLoading(false);
    };

    const loadAnswer = async (askQuestion = question) => {
        const trimmed = askQuestion.trim();
        if (!trimmed || loading) {
            return;
        }
        const newAnswers: Answer[] = [...answers, { role: Role.User, content: trimmed, id: answers.length }];
        setAnswers(newAnswers);

        setQuestion('');

        const resp = await getAnswerResponse(trimmed);

        const contentType = resp.headers.get('Content-Type') || '';
        if (contentType.includes('application/json')) {
            await processNonStreamResponse(resp, newAnswers);
        } else {
            await processStreamResponse(resp, newAnswers);
        }
    };


    const loadAnswerRef = useRef(loadAnswer);
    loadAnswerRef.current = loadAnswer;

    useEffect(() => {
        if (copilotParam) {
            loadAnswerRef.current(copilotParam);
            setSearchParams(prev => {
                prev.set('copilot', '');
                return prev;
            }, { replace: true });
        }
    }, [copilotParam, setSearchParams]);

    const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        loadAnswer();
    };

    const headerMenu = (
        <Menu>
            <ListAction icon="recycle" onClick={() => setAnswers([])}>
                {({ wrap }) => (
                    <PopoverConfirm
                        target={wrap(translate('clear-all'))}
                    >
                        {translate('confirm-clear-all-question')}
                    </PopoverConfirm>
                )}
            </ListAction>
        </Menu>
    );

    const handleRemovePair = (removeId: Answer['id']) => {
        const index = answers.findIndex(({ id }) => id === removeId);
        if (index === -1) {
            return;
        }
        if (index === 0) {
            return;
        }

        setAnswers(answers.filter((_, i) => i !== index && i !== index - 1));
    };

    return (
        <div className="plesk-copilot-chat-container">
            <div className="plesk-copilot-chat-header">
                <Toolbar>
                    <Heading level={2}>
                        <Icon src={require('images/AI-colored.svg')} />{' '}
                        {translate('title')}
                    </Heading>
                    <ToolbarExpander />
                    <Dropdown menu={headerMenu}>
                        <Button icon="three-dots-horizontal" ghost tooltip={translate('chat-menu')} tooltipAsLabel />
                    </Dropdown>
                    <Button tooltip={translate('close-button-title')} tooltipAsLabel ghost icon="cross-mark" onClick={() => setIsRightSidebarOpen(false)} />
                </Toolbar>
            </div>
            <div className="plesk-copilot-chat-messages" ref={listRef}>
                {answers.map(message => <MessageItem key={message.id} message={message} onRemove={handleRemovePair} />)}
                {loading && (
                    <div className="msg-row left">
                        <div className="assistant-card">
                            <div className="assistant-content">
                                <ContentLoader
                                    className="plesk-copilot-loading-indicator"
                                    text={<Translate content={'loader.text'} />}
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>
            <form
                onSubmit={onSubmit}
                className="plesk-copilot-chat-input-row"
                ref={inputRef}
            >
                <TextArea
                    value={question}
                    placeholder={String(translate('chat-input.placeholder'))}
                    disabled={loading}
                    onChange={e => setQuestion(e.target.value)}
                    onKeyDown={e => {
                        if (e.key === 'Enter' && !e.shiftKey && question.trim()) {
                            e.preventDefault();
                            loadAnswer();
                        }
                    }}
                    size="fill"
                    className="plesk-copilot-chat-input"
                    autoheight
                    rows={1}
                />
                {!!question && <Button ghost icon="arrow-right-circle-filled" type="submit" disabled={loading} />}
            </form>
        </div>
    );
};

export default Chat;
